import { toast } from '@inno-ui/toast'
import { INNOSCRIPTA_INTERNAL_ORGANIZATION_ID } from 'shared/constants/user'
import { getContainerWindowMeta, getIsInsideWindow } from 'shared/utils'

import { LoggerDB } from './LoggerDB'
import type {
  EventType,
  LogAppContext,
  LogCategory,
  LogContext,
  LogEntry,
  LogEventMap,
  LogExport,
  LogLevel,
  LogOptions,
  LogOutcome,
  LoggerHealth,
  SessionSnapshot
} from './logger.types'
import {
  REDACTION_POLICY_VERSION,
  sanitizePayload,
  sanitizeUrl,
  serializeError
} from './sanitize'
import { resolveStack } from './stackResolver'

const LOG_RETENTION_MS = 24 * 60 * 60 * 1000
const MAX_PERSISTED_ENTRIES = 5_000
const MAX_PERSISTED_BYTES = 10 * 1024 * 1024
const MAX_MEMORY_ENTRIES = 500
const LIMIT_CHECK_EVERY_WRITES = 100
const TAB_ID_KEY = 'app-logger-tab-id'

const EVENT_TYPE_PREFIXES: Record<string, EventType> = {
  auth: 'auth',
  navigation: 'navigation',
  storage: 'storage',
  system: 'system'
}

const CATEGORY_PREFIXES: Record<string, LogCategory> = {
  api: 'api',
  auth: 'auth',
  desktop: 'business',
  detached_app: 'ui',
  federation: 'federation',
  js: 'runtime',
  localStorage: 'storage',
  navigation: 'navigation',
  performance: 'performance',
  product_updates: 'business',
  react: 'runtime',
  service_worker: 'service-worker',
  socket: 'socket',
  storage: 'storage',
  system: 'system',
  window: 'ui'
}

type EntrySubscriber = (entry: LogEntry) => void
type Forwarder = (entry: LogEntry) => boolean

function randomId(): string {
  return crypto.randomUUID()
}

function inferEventType(event: string): EventType {
  const prefix = event.split('.')[0]
  return EVENT_TYPE_PREFIXES[prefix] ?? 'action'
}

function inferCategory(event: string): LogCategory {
  const prefix = event.split('.')[0]
  return CATEGORY_PREFIXES[prefix] ?? 'business'
}

function inferOutcome(
  event: string,
  payload: Record<string, unknown>,
  level: LogLevel
): LogOutcome | undefined {
  if (level === 'error') return 'failure'
  if (typeof payload.success === 'boolean') {
    return payload.success ? 'success' : 'failure'
  }
  if (/\.(failed|failure|error)$/.test(event)) return 'failure'
  if (/\.(completed|success|succeeded)$/.test(event)) return 'success'
  if (/\.(cancelled|canceled|aborted)$/.test(event)) return 'cancelled'
  return undefined
}

function getTabId(): string {
  try {
    const current = sessionStorage.getItem(TAB_ID_KEY)
    if (current) return current
    const next = randomId()
    sessionStorage.setItem(TAB_ID_KEY, next)
    return next
  } catch {
    return randomId()
  }
}

function getContextType(): LogContext['contextType'] {
  try {
    return window.parent !== window || getIsInsideWindow() ? 'iframe' : 'main'
  } catch {
    return 'iframe'
  }
}

function appContext(): LogAppContext {
  return {
    name: process.env.REACT_APP_NAME ?? 'container_navigation_app_v2',
    version: process.env.REACT_APP_VERSION ?? '1.0.0',
    buildId: process.env.REACT_APP_BUILD_ID,
    environment:
      process.env.REACT_APP_ENVIRONMENT ?? process.env.NODE_ENV ?? 'development'
  }
}

function buildSessionSnapshot(
  sessionId: string,
  tabId: string,
  contextId: string,
  contextType: LogContext['contextType'],
  userId?: number,
  organizationId?: number
): SessionSnapshot {
  const ua = navigator.userAgent
  return {
    sessionId,
    tabId,
    contextId,
    contextType,
    startedAt: Date.now(),
    userAgent: ua,
    os: /windows/i.test(ua)
      ? 'Windows'
      : /(?:iphone|ipad|ipod)/i.test(ua)
        ? 'iOS'
        : /android/i.test(ua)
          ? 'Android'
          : /mac/i.test(ua)
            ? 'macOS'
            : /linux/i.test(ua)
              ? 'Linux'
              : 'Unknown',
    browser:
      /edg\/\S+/i.exec(ua)?.[0] ??
      /chrome\/\S+/i.exec(ua)?.[0] ??
      /firefox\/\S+/i.exec(ua)?.[0] ??
      /version\/\S+.*safari/i.exec(ua)?.[0] ??
      'Unknown',
    viewport: { width: window.innerWidth, height: window.innerHeight },
    screen: {
      width: window.screen.width,
      height: window.screen.height,
      dpr: window.devicePixelRatio
    },
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    locale: navigator.language,
    userId,
    organizationId,
    url: sanitizeUrl(window.location.href),
    app: appContext()
  }
}

export class LoggerService {
  private db: LoggerDB
  private sessionId = randomId()
  private readonly tabId = getTabId()
  private readonly contextId = randomId()
  private readonly contextType = getContextType()
  private snapshot: SessionSnapshot
  private pending = new Set<Promise<void>>()
  private memoryEntries: LogEntry[] = []
  private pausedEntries: LogEntry[] = []
  private subscribers = new Set<EntrySubscriber>()
  private recentErrors = new Map<string, number>()
  private forwarder?: Forwarder
  private persistencePaused = false
  private operationChain: Promise<void> = Promise.resolve()
  private writesSinceLimitCheck = 0
  private redactedValueCount = 0
  private truncatedValueCount = 0
  private health: LoggerHealth = {
    status: 'healthy',
    pendingWrites: 0,
    bufferedEntries: 0,
    droppedEntries: 0
  }

  constructor() {
    this.snapshot = buildSessionSnapshot(
      this.sessionId,
      this.tabId,
      this.contextId,
      this.contextType
    )
    this.db = new LoggerDB((context, error) => {
      this.markPersistenceFailure(context, error)
    })
    this.persistSession()
    this.enforceLimits()
  }

  setUser(userId: number, organizationId: number): void {
    this.setActor(userId, organizationId)
  }

  setActor(userId: number, organizationId?: number): void {
    const wasAllowed = this.isCollectionAllowed()
    this.snapshot = { ...this.snapshot, userId, organizationId }
    if (wasAllowed && !this.isCollectionAllowed()) {
      // Org resolved to something other than the internal org after logs may
      // already have accumulated (e.g. pre-login events) — wipe them.
      void this.clear()
      return
    }
    this.persistSession()
  }

  /**
   * Logging is restricted to the internal Innoscripta organization
   * (org id 1). While the actor's organization is unknown (pre-login),
   * collection is allowed; once an organization other than 1 is confirmed,
   * all further collection stops.
   */
  private isCollectionAllowed(): boolean {
    const orgId = this.snapshot.organizationId
    return orgId == null || orgId === INNOSCRIPTA_INTERNAL_ORGANIZATION_ID
  }

  clearActor(): void {
    const {
      userId: _userId,
      organizationId: _organizationId,
      ...snapshot
    } = this.snapshot
    this.snapshot = snapshot
    this.persistSession()
  }

  startSession(): void {
    this.sessionId = randomId()
    this.snapshot = buildSessionSnapshot(
      this.sessionId,
      this.tabId,
      this.contextId,
      this.contextType,
      this.snapshot.userId,
      this.snapshot.organizationId
    )
    this.persistSession()
  }

  endSession(reason: string): void {
    this.track('system.session_end', { reason }, 'system', 'info')
    this.snapshot = {
      ...this.snapshot,
      endedAt: Date.now(),
      endReason: reason
    }
    this.persistSession()
  }

  track(
    event: string,
    payload: Record<string, unknown> = {},
    type?: EventType,
    level?: LogLevel
  ): void {
    try {
      this.record(
        event,
        payload,
        {
          category: inferCategory(event),
          level: level ?? 'info'
        },
        type ?? inferEventType(event)
      )
    } catch {
      // Tracking is best effort and must not interrupt application behavior.
    }
  }

  trackEvent<K extends keyof LogEventMap>(
    event: K,
    payload: LogEventMap[K],
    options: LogOptions = {}
  ): void {
    this.record(event, payload, options, inferEventType(event))
  }

  log(
    event: string,
    attributes: Record<string, unknown> = {},
    options: LogOptions = {}
  ): void {
    this.record(event, attributes, options, inferEventType(event))
  }

  debug(event: string, payload: Record<string, unknown> = {}): void {
    this.record(event, payload, { level: 'debug' }, inferEventType(event))
  }

  warn(event: string, payload: Record<string, unknown> = {}): void {
    this.record(event, payload, { level: 'warn' }, inferEventType(event))
  }

  error(event: string, payload: Record<string, unknown> = {}): void {
    this.record(
      event,
      payload,
      { category: inferCategory(event), level: 'error', outcome: 'failure' },
      'error'
    )
  }

  exception(
    event: string,
    error: unknown,
    attributes: Record<string, unknown> = {},
    options: Omit<LogOptions, 'level' | 'outcome'> = {}
  ): void {
    const serialized = serializeError(error)
    const fingerprint = `${event}:${serialized.name}:${serialized.message}:${serialized.stack?.split('\n', 2)[1] ?? ''}`
    const now = Date.now()
    const lastSeen = this.recentErrors.get(fingerprint)
    if (lastSeen != null && now - lastSeen < 2_000) return
    this.recentErrors.set(fingerprint, now)
    if (this.recentErrors.size > 100) {
      for (const [key, timestamp] of this.recentErrors) {
        if (now - timestamp >= 2_000) this.recentErrors.delete(key)
      }
    }
    this.record(
      event,
      attributes,
      { ...options, level: 'error', outcome: 'failure' },
      'error',
      serialized
    )
  }

  trackRaw(
    event: string,
    payload: Record<string, unknown> = {},
    type?: EventType,
    level?: LogLevel
  ): void {
    this.track(event, { ...payload, ...this.windowPayload() }, type, level)
  }

  errorRaw(event: string, payload: Record<string, unknown> = {}): void {
    this.error(event, { ...payload, ...this.windowPayload() })
  }

  ingest(entry: LogEntry): void {
    if (!this.isCollectionAllowed()) return
    if (!entry || typeof entry !== 'object') return
    if (typeof entry.id !== 'string' || typeof entry.event !== 'string') return
    if (
      !Number.isFinite(entry.timestamp) ||
      typeof entry.sessionId !== 'string'
    )
      return
    const sanitized = sanitizePayload({
      event: entry.event,
      payload: entry.payload ?? {},
      app: entry.app,
      error: entry.error,
      correlation: entry.correlation
    })
    const sanitizedContext = sanitizePayload({
      windowTitle: entry.context?.windowTitle,
      route: entry.context?.route
    }).value
    this.redactedValueCount += sanitized.stats.redactedValueCount
    this.truncatedValueCount += sanitized.stats.truncatedValueCount
    const envelope = sanitized.value
    const allowedLevels: LogLevel[] = ['debug', 'info', 'warn', 'error']
    const allowedTypes: EventType[] = [
      'error',
      'navigation',
      'action',
      'auth',
      'storage',
      'system'
    ]
    this.persistEntry({
      ...entry,
      schemaVersion: 2,
      event:
        typeof envelope.event === 'string' ? envelope.event : 'invalid.event',
      type: allowedTypes.includes(entry.type) ? entry.type : 'action',
      level: allowedLevels.includes(entry.level) ? entry.level : 'info',
      payload:
        envelope.payload &&
        typeof envelope.payload === 'object' &&
        !Array.isArray(envelope.payload)
          ? (envelope.payload as Record<string, unknown>)
          : {},
      context: entry.context
        ? {
            ...entry.context,
            sessionId:
              typeof entry.context.sessionId === 'string'
                ? entry.context.sessionId.slice(0, 128)
                : entry.sessionId.slice(0, 128),
            tabId:
              typeof entry.context.tabId === 'string'
                ? entry.context.tabId.slice(0, 128)
                : 'unknown',
            contextId:
              typeof entry.context.contextId === 'string'
                ? entry.context.contextId.slice(0, 128)
                : entry.sessionId.slice(0, 128),
            windowId: entry.context.windowId?.slice(0, 128),
            windowTitle: sanitizedContext.windowTitle as string | undefined,
            route: sanitizedContext.route as string | undefined
          }
        : undefined,
      app: envelope.app as LogAppContext | undefined,
      error: envelope.error as LogEntry['error'],
      correlation: envelope.correlation as LogEntry['correlation'],
      category: entry.category ?? inferCategory(entry.event)
    })
  }

  ingestSession(snapshot: SessionSnapshot): void {
    if (!this.isCollectionAllowed()) return
    if (!snapshot || typeof snapshot !== 'object') return
    if (
      typeof snapshot.sessionId !== 'string' ||
      snapshot.sessionId.length === 0 ||
      !Number.isFinite(snapshot.startedAt)
    )
      return
    const sanitized = sanitizePayload({
      userAgent: snapshot.userAgent,
      os: snapshot.os,
      browser: snapshot.browser,
      timezone: snapshot.timezone,
      locale: snapshot.locale,
      url: snapshot.url,
      endReason: snapshot.endReason,
      app: snapshot.app
    })
    this.redactedValueCount += sanitized.stats.redactedValueCount
    this.truncatedValueCount += sanitized.stats.truncatedValueCount
    const value = sanitized.value
    const contextTypes: LogContext['contextType'][] = [
      'main',
      'iframe',
      'worker',
      'service-worker'
    ]
    const safeSnapshot: SessionSnapshot = {
      sessionId: snapshot.sessionId.slice(0, 128),
      tabId: snapshot.tabId?.slice(0, 128),
      contextId: snapshot.contextId?.slice(0, 128),
      contextType:
        snapshot.contextType && contextTypes.includes(snapshot.contextType)
          ? snapshot.contextType
          : undefined,
      startedAt: snapshot.startedAt,
      endedAt:
        snapshot.endedAt != null && Number.isFinite(snapshot.endedAt)
          ? snapshot.endedAt
          : undefined,
      endReason: value.endReason as string | undefined,
      userAgent:
        typeof value.userAgent === 'string' ? value.userAgent : 'Unknown',
      os: typeof value.os === 'string' ? value.os : 'Unknown',
      browser: typeof value.browser === 'string' ? value.browser : 'Unknown',
      viewport: {
        width: Number.isFinite(snapshot.viewport?.width)
          ? snapshot.viewport.width
          : 0,
        height: Number.isFinite(snapshot.viewport?.height)
          ? snapshot.viewport.height
          : 0
      },
      screen: {
        width: Number.isFinite(snapshot.screen?.width)
          ? snapshot.screen.width
          : 0,
        height: Number.isFinite(snapshot.screen?.height)
          ? snapshot.screen.height
          : 0,
        dpr: Number.isFinite(snapshot.screen?.dpr) ? snapshot.screen.dpr : 1
      },
      timezone: typeof value.timezone === 'string' ? value.timezone : 'Unknown',
      locale: typeof value.locale === 'string' ? value.locale : 'Unknown',
      userId: Number.isFinite(snapshot.userId) ? snapshot.userId : undefined,
      organizationId: Number.isFinite(snapshot.organizationId)
        ? snapshot.organizationId
        : undefined,
      url: typeof value.url === 'string' ? value.url : '',
      app: value.app as LogAppContext | undefined
    }
    const db = this.db as LoggerDB & {
      writeSession?: (next: SessionSnapshot) => Promise<void>
    }
    if (typeof db.writeSession !== 'function') return
    this.trackPromise(
      db.writeSession(safeSnapshot).catch(error => {
        this.markPersistenceFailure('ingest_session', error)
      })
    )
  }

  setForwarder(forwarder?: Forwarder): void {
    this.forwarder = forwarder
  }

  subscribe(subscriber: EntrySubscriber): () => void {
    this.subscribers.add(subscriber)
    return () => this.subscribers.delete(subscriber)
  }

  noteDropped(count = 1): void {
    this.health = {
      ...this.health,
      status: 'degraded',
      droppedEntries: this.health.droppedEntries + count
    }
  }

  private record(
    event: string,
    payload: Record<string, unknown>,
    options: LogOptions,
    type: EventType,
    error?: ReturnType<typeof serializeError>
  ): void {
    try {
      const level = options.level ?? 'info'
      const sanitized = sanitizePayload(payload)
      this.redactedValueCount += sanitized.stats.redactedValueCount
      this.truncatedValueCount += sanitized.stats.truncatedValueCount
      const context = this.currentContext()
      const entry: LogEntry = {
        schemaVersion: 2,
        id: randomId(),
        timestamp: Date.now(),
        type,
        category: options.category ?? inferCategory(event),
        level,
        outcome: options.outcome ?? inferOutcome(event, payload, level),
        event,
        payload: sanitized.value,
        error,
        correlation: options.correlation,
        sessionId: this.sessionId,
        context,
        app: this.snapshot.app ?? appContext()
      }
      this.persistEntry(entry)
    } catch {
      // Logging is best effort and must not interrupt application behavior.
    }
  }

  private currentContext(): LogContext {
    const meta = getIsInsideWindow() ? getContainerWindowMeta() : undefined
    return {
      sessionId: this.sessionId,
      tabId: this.tabId,
      contextId: this.contextId,
      contextType: this.contextType,
      userId: this.snapshot.userId,
      organizationId: this.snapshot.organizationId,
      windowId: meta?.windowId,
      windowTitle: meta?.title,
      route: sanitizeUrl(window.location.href)
    }
  }

  private windowPayload(): {
    windowed: boolean
    windowId?: string
    windowTitle?: string
  } {
    const context = this.currentContext()
    return {
      windowed: context.contextType === 'iframe',
      windowId: context.windowId,
      windowTitle: context.windowTitle
    }
  }

  private persistEntry(entry: LogEntry): void {
    if (!this.isCollectionAllowed()) return
    this.memoryEntries.push(entry)
    if (this.memoryEntries.length > MAX_MEMORY_ENTRIES)
      this.memoryEntries.shift()
    this.health = {
      ...this.health,
      bufferedEntries: this.memoryEntries.length
    }
    this.subscribers.forEach(subscriber => {
      try {
        subscriber(entry)
      } catch {
        // Subscribers must never interfere with logging.
      }
    })

    if (this.forwarder?.(entry)) return
    if (this.persistencePaused) {
      this.pausedEntries.push(entry)
      return
    }
    this.persistEntryToDB(entry)
  }

  private persistEntryToDB(entry: LogEntry): void {
    const promise = this.db.write(entry).then(
      () => {
        this.health = {
          ...this.health,
          status: this.health.lastPersistenceError ? 'degraded' : 'healthy',
          lastSuccessfulWriteAt: Date.now()
        }
        this.writesSinceLimitCheck += 1
        if (this.writesSinceLimitCheck >= LIMIT_CHECK_EVERY_WRITES) {
          this.writesSinceLimitCheck = 0
          this.enforceLimits()
        }
      },
      error => this.markPersistenceFailure('write', error)
    )
    this.trackPromise(promise)
  }

  private persistSession(): void {
    if (!this.isCollectionAllowed()) return
    const db = this.db as LoggerDB & {
      writeSession?: (snapshot: SessionSnapshot) => Promise<void>
    }
    if (typeof db.writeSession !== 'function') return
    this.trackPromise(
      db.writeSession(this.snapshot).catch(error => {
        this.markPersistenceFailure('write_session', error)
      })
    )
  }

  private enforceLimits(): void {
    const db = this.db as LoggerDB & {
      enforceLimits?: (maxEntries: number, maxBytes: number) => Promise<void>
    }
    const limitPromise =
      typeof db.enforceLimits === 'function'
        ? db.enforceLimits(MAX_PERSISTED_ENTRIES, MAX_PERSISTED_BYTES)
        : Promise.resolve()
    this.trackPromise(
      Promise.all([this.db.prune(LOG_RETENTION_MS), limitPromise])
        .then(() => undefined)
        .catch(error => {
          this.markPersistenceFailure('enforce_limits', error)
        })
    )
  }

  private trackPromise(promise: Promise<void>): void {
    this.pending.add(promise)
    this.health = { ...this.health, pendingWrites: this.pending.size }
    promise.finally(() => {
      this.pending.delete(promise)
      this.health = { ...this.health, pendingWrites: this.pending.size }
    })
  }

  private markPersistenceFailure(context: string, error: unknown): void {
    const message = error instanceof Error ? error.message : String(error)
    this.health = {
      ...this.health,
      status: 'degraded',
      lastPersistenceError: `${context}: ${message}`
    }
    LoggerDB.devWarn(context, error)
  }

  async flush(): Promise<void> {
    while (this.pending.size > 0) {
      // Writes can enqueue retention/session work while the current batch settles.
      // eslint-disable-next-line no-await-in-loop
      await Promise.allSettled([...this.pending])
    }
  }

  private async withPersistenceBarrier<T>(
    operation: () => Promise<T>
  ): Promise<T> {
    let result!: T
    let operationError: unknown
    let operationFailed = false
    const run = async () => {
      this.persistencePaused = true
      await this.flush()
      try {
        result = await operation()
      } catch (error) {
        operationFailed = true
        operationError = error
      } finally {
        this.persistencePaused = false
        const queued = this.pausedEntries.splice(0)
        queued.forEach(entry => this.persistEntryToDB(entry))
      }
    }
    const scheduled = this.operationChain.then(run, run)
    this.operationChain = scheduled.then(
      () => undefined,
      () => undefined
    )
    await scheduled
    if (operationFailed) throw operationError
    return result
  }

  async export(): Promise<void> {
    try {
      await this.withPersistenceBarrier(async () => {
        const entries = await this.db.readAll()
        entries.sort((a, b) => a.timestamp - b.timestamp)

        const resolvedEntries = await Promise.all(
          entries.map(async entry => {
            const rawStack = entry.error?.stack ?? entry.payload.stack
            if (typeof rawStack !== 'string') return entry
            try {
              const resolvedStack = await resolveStack(rawStack)
              return { ...entry, payload: { ...entry.payload, resolvedStack } }
            } catch {
              return entry
            }
          })
        )

        const db = this.db as LoggerDB & {
          readSessions?: () => Promise<SessionSnapshot[]>
        }
        const sessions =
          typeof db.readSessions === 'function'
            ? await db.readSessions()
            : [this.snapshot]
        const data: LogExport = {
          schemaVersion: 2,
          snapshot: this.getSnapshot(),
          sessions,
          entries: resolvedEntries,
          health: this.getHealth(),
          redaction: {
            policyVersion: REDACTION_POLICY_VERSION,
            redactedValueCount: this.redactedValueCount,
            truncatedValueCount: this.truncatedValueCount
          }
        }
        const json = JSON.stringify(data, null, 2)
        const blob = new Blob([json], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `logs-${new Date().toISOString().replace(/:/g, '-')}.json`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      })
    } catch (err) {
      // Last-resort reporting: the logger itself may be unavailable here.
      // eslint-disable-next-line no-console
      console.error('[LoggerService] export failed:', err)
      toast.error({
        title: 'Export failed',
        subTitle: 'Could not export logs. Please try again.'
      })
    }
  }

  async clear(): Promise<void> {
    await this.withPersistenceBarrier(async () => {
      this.memoryEntries = []
      this.health = { ...this.health, bufferedEntries: 0 }
      await this.db.clear()
      this.persistSession()
    }).catch(error => this.markPersistenceFailure('clear', error))
  }

  async getEntryCount(): Promise<number> {
    return this.db.count()
  }

  async readAll(): Promise<LogEntry[]> {
    await this.flush()
    try {
      return await this.db.readAll()
    } catch (error) {
      this.markPersistenceFailure('read_all', error)
      return [...this.memoryEntries]
    }
  }

  getSnapshot(): SessionSnapshot {
    return structuredClone(this.snapshot)
  }

  getHealth(): LoggerHealth {
    return { ...this.health }
  }
}

export const logger = new LoggerService()
